/**
 * CRM Client Messages Page - Secure messaging with the accounting firm.
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crmPortalService } from '@/services/api/crm-portal';
import { crmAuth } from '@/services/auth/crm-auth';
import { Button } from '@/components/ui/button';
import { Send, Loader2, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

export function CRMMessagesPage() {
  const queryClient = useQueryClient();
  const client = crmAuth.getClient();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-messages'],
    queryFn: () => crmPortalService.getMessages(),
    refetchInterval: 15000,
  });

  const messages = data?.messages || [];

  const sendMutation = useMutation({
    mutationFn: (content: string) => crmPortalService.sendMessage(content),
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['crm-messages'] });
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = () => {
    if (!message.trim() || sendMutation.isPending) return;
    sendMutation.mutate(message.trim());
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-3xl mx-auto">
      <div className="px-4 py-3 border-b">
        <h1 className="text-lg font-semibold">Messages</h1>
        <p className="text-xs text-muted-foreground">Secure messaging with your accountant</p>
      </div>

      {/* Messages list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 [&::-webkit-scrollbar]:hidden">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs mt-1">Send a message to start a conversation with your accountant.</p>
          </div>
        ) : (
          messages.map((msg: any) => (
            <div
              key={msg.id}
              className={cn(
                'flex',
                msg.sender_type === 'client' ? 'justify-end' : 'justify-start'
              )}
            >
              <div className={cn(
                'max-w-[80%] rounded-2xl px-4 py-2.5',
                msg.sender_type === 'client'
                  ? 'bg-[#5048e5] text-white rounded-br-md'
                  : 'bg-muted rounded-bl-md'
              )}>
                {msg.sender_type === 'firm' && (
                  <p className="text-[10px] font-medium mb-0.5 opacity-70">{msg.sender_name || 'Your Accountant'}</p>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                <p className={cn(
                  'text-[10px] mt-1',
                  msg.sender_type === 'client' ? 'text-white/60' : 'text-muted-foreground'
                )}>
                  {new Date(msg.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <div className="border-t p-3">
        <div className="flex gap-2">
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Type a message..."
            className="flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5048e5]/30"
            rows={2}
          />
          <Button
            onClick={handleSend}
            disabled={!message.trim() || sendMutation.isPending}
            className="bg-[#5048e5] hover:bg-[#443fcc] h-auto"
          >
            {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
